<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import Avatar from 'next/avatar/Avatar.vue';

const props = defineProps({
  card: { type: Object, required: true },
  funnelId: { type: [Number, String], required: true },
});

const emit = defineEmits(['edit', 'delete', 'won', 'lost']);

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const openCardDetail = () => {
  router.push({
    name: 'crm_card_detail',
    params: {
      accountId: route.params.accountId,
      funnelId: props.funnelId,
      cardId: props.card.id,
    },
  });
};

const formattedValue = computed(() => {
  if (!props.card.value || Number(props.card.value) === 0) return null;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(props.card.value);
});

const statusDotClass = computed(() => {
  switch (props.card.status) {
    case 'won':
      return 'bg-green-500';
    case 'lost':
      return 'bg-red-500';
    default:
      return 'bg-n-blue-9';
  }
});

const daysInStage = computed(() => props.card.days_in_stage ?? null);

const daysBadgeClass = computed(() => {
  const d = daysInStage.value;
  if (d === null) return '';
  if (d <= 3)
    return 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400';
  if (d <= 7)
    return 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
  return 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400';
});
</script>

<template>
  <div
    class="group bg-white dark:bg-n-slate-2 shadow-sm hover:shadow-md rounded-lg p-3 cursor-grab active:cursor-grabbing transition-shadow duration-150"
    draggable="true"
    @dragstart="$event.dataTransfer.setData('cardId', String(card.id))"
  >
    <!-- Title row + actions -->
    <div class="flex items-start justify-between gap-2">
      <div class="flex items-center gap-1.5 min-w-0">
        <span
          class="size-2 rounded-full flex-shrink-0"
          :class="statusDotClass"
        />
        <h4
          class="text-sm font-medium text-n-slate-12 leading-5 line-clamp-2 cursor-pointer hover:text-n-brand hover:underline"
          @click.stop="openCardDetail"
        >
          {{ card.title }}
        </h4>
      </div>
      <div
        class="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
      >
        <button
          v-if="card.status === 'open'"
          class="size-6 grid place-content-center rounded-md hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
          :title="$t('CRM.DETAIL.MARK_WON')"
          @click.stop="emit('won', card)"
        >
          <span
            class="i-lucide-trophy size-3.5 text-green-600 dark:text-green-400"
          />
        </button>
        <button
          v-if="card.status === 'open'"
          class="size-6 grid place-content-center rounded-md hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
          :title="$t('CRM.DETAIL.MARK_LOST')"
          @click.stop="emit('lost', card)"
        >
          <span
            class="i-lucide-x-circle size-3.5 text-red-600 dark:text-red-400"
          />
        </button>
        <button
          class="size-6 grid place-content-center rounded-md hover:bg-n-alpha-2 transition-colors"
          @click.stop="emit('edit', card)"
        >
          <span class="i-lucide-pencil size-3.5 text-n-slate-10" />
        </button>
        <button
          class="size-6 grid place-content-center rounded-md hover:bg-n-ruby-2 transition-colors"
          @click.stop="emit('delete', card)"
        >
          <span
            class="i-lucide-trash-2 size-3.5 text-n-slate-10 hover:text-n-ruby-11"
          />
        </button>
      </div>
    </div>

    <!-- Description -->
    <p
      v-if="card.description"
      class="mt-1 text-xs text-n-slate-11 line-clamp-2"
    >
      {{ card.description }}
    </p>

    <!-- Footer: contact, days, value, agent -->
    <div class="mt-2 flex items-center justify-between gap-2">
      <div class="flex items-center gap-2 min-w-0">
        <div v-if="card.contact" class="flex items-center gap-1.5 min-w-0">
          <Avatar
            :name="card.contact.name"
            :src="card.contact.thumbnail"
            :size="18"
            rounded-full
          />
          <span class="text-[11px] text-n-slate-10 truncate">
            {{ card.contact.name }}
          </span>
        </div>
        <span
          v-if="daysInStage !== null"
          class="text-[10px] font-medium px-1.5 py-0.5 rounded-full flex-shrink-0"
          :class="daysBadgeClass"
        >
          {{ t('CRM.DAYS_SHORT', { count: daysInStage }) }}
        </span>
      </div>
      <div class="flex items-center gap-1.5 ml-auto flex-shrink-0">
        <span
          v-if="formattedValue"
          class="text-xs font-semibold text-green-600 dark:text-green-400"
        >
          {{ formattedValue }}
        </span>
        <div v-if="card.assignee" class="flex flex-col items-center gap-0.5">
          <Avatar
            :name="card.assignee.name"
            :src="card.assignee.thumbnail"
            :size="24"
            rounded-full
          />
          <span class="text-[9px] text-n-slate-9 leading-none">
            {{ card.assignee.name.split(' ')[0] }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
